import { CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { usePaymentMethods } from "@/features/payments";
import { type PaymentAccountCard } from "@/features/payments/types/payment-account-cards";
import { formatCurrency } from "@/lib/currency-formatter";

interface ManageCardProps {
  card: PaymentAccountCard;
  hideManageButton?: boolean;
}

export const ManageCard = (props: ManageCardProps) => {
  const { card, hideManageButton = false } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { paymentMethodsLoading, paymentMethodsError } = usePaymentMethods();

  if (paymentMethodsError || paymentMethodsLoading) {
    return null;
  }

  // A null balance is a Hidden Balance: PPS sends it for Spend Cards and
  // read-only cards, and it must never be rendered, not even as 0.00.
  const hasBalance = card.balance !== null;
  const isReadOnly = card.is_read_only;
  const showBalance = !isReadOnly && hasBalance;

  return (
    <div className="rounded-lg bg-primary text-white">
      <div className="relative p-4 pb-5 bg-no-repeat">
        <div className="flex flex-row gap-2 items-center">
          <div>
            {!showBalance ? (
              <div className="flex justify-between items-center">
                <h5 className="text-2xl capitalize">{card.title}</h5>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <h5 className="text-2xl font-semibold font-mono">
                    {formatCurrency(Number(card.balance), card.currency)}
                  </h5>
                </div>

                <p className="text-sm">{t("vcc_availableBalance")}</p>
              </>
            )}

            {!hideManageButton && (
              <Button
                className="mt-4 bg-transparent"
                variant="outline"
                onClick={() => {
                  navigate("/card/details");
                }}
                aria-label="Manage your cards"
              >
                <CreditCard />
                {t("cards_manage")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
